<template>
  <div>
    <div
      v-if="isProcessing"
      class="h-40 bg-surface-200/10 dark:bg-surface-700/30 flex flex-col items-center justify-center text-center border dark:border-surface-700 rounded-md"
    >
      <i class="pi pi-image text-lg animate-[bounce_1s_400ms_ease-in-out_infinite]" />

      <p class="text-sm mt-1">Generating image</p>

      <div class="py-4 w-3/4">
        <ProgressBar :value="progress" :show-value="false" style="height: 5px" />
      </div>
    </div>
    <ContentEditMediaPreview
      v-else-if="imageUrl"
      :url="imageUrl"
      :class="small ? 'px-6 @sm:px-10' : ''"
      class="pb-6"
      media-type="image"
      @cancel="imageUrl = ''"
      @accept="handleAttachmentReady"
    />
    <template v-else>
      <MATextarea
        id="prompt"
        v-model="prompt"
        :rows="5"
        :label="$t('GENERATE_IMAGE.name')"
      />
      <div class="flex justify-end">
        <AppButton :label="$t('generate')" variant="secondary" @click="generateImage()" />
      </div>
    </template>

    <div v-if="!imageUrl" class="mt-4">
      <p class="font-semibold mb-2">
        <i class="pi pi-sparkles" /> Recently generated images
      </p>

      <AppLoading v-if="isLoadingImages" title-class="hidden" wrapper-class="h-32" />
      <div
        v-else-if="!recentImages.length"
        class="text-sm text-surface-500 p-4 py-6 text-center rounded-md bg-surface-200/10 dark:bg-surface-700/30 dark:text-surface-400 border dark:border-surface-700 flex flex-col items-center justify-center"
      >
        <i class="pi pi-image" />
        <p class="text-center text-sm">
          No images generated yet. Your recently generated images will appear here.
        </p>
      </div>
      <div v-else class="grid gap-1" :class="small ? 'grid-cols-3' : 'grid-cols-2'">
        <template v-for="(image, index) in recentImages">
          <div
            v-if="image"
            :key="index"
            class="relative overflow-hidden bg-surface-200 dark:bg-surface-700 w-full aspect-square"
            :class="images[image] ? 'opacity-50 cursor-not-allowed' : ''"
            role="button"
            @click="imageUrl = images[image] ? '' : image"
          >
            <img
              :src="image"
              class="object-cover border dark:border-surface-700 duration-500 hover:scale-110 hover:brightness-75 w-full aspect-square"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- <pre>{{ recentImages }}</pre> -->
  </div>
</template>

<script setup lang="ts">
import MATextarea from "@/components/primevueComponents/MATextarea.vue";
import type { GenerateImageResult } from "@/types/task";
import { useToast } from "vue-toast-notification";
import ContentEditMediaPreview from "./ContentEditMediaPreview.vue";
import ProgressBar from "@/volt/ProgressBar.vue";
import AppLoading from "@/components/shared/AppLoading.vue";

defineProps<{
  images: Record<string, boolean>;
  small?: boolean;
}>();

const emit = defineEmits<{
  done: [string];
}>();

const $toast = useToast(defaultToastOptions);
const prompt = ref("");
const { isPending, mutate } = createNewSingleTask();
const taskId = ref("");
const imageUrl = ref("");
const { data, isLoading } = useSingleTask(taskId);
const {
  data: tasks,
  isLoading: isLoadingImages,
  refetch: refetchContent,
} = useContentListRequest({
  __limit: 100,
  taskType: "website/tools/generate-image",
});
const { progress, endProgress, startProgress, setProgress } = useFakeProgress({
  maxValue: 99,
  increments: 1,
  minValue: 5,
  interval: 700,
  auto: false,
});

const isProcessing = computed(
  () =>
    isPending.value ||
    isLoading.value ||
    data.value?.queued === 1 ||
    data.value?.running === 1
);

const recentImages = computed(() => {
  if (!tasks.value) return [];
  const recentTasks = tasks.value.filter(
    task =>
      task.taskType === "website/tools/generate-image" &&
      (task.resultData as GenerateImageResult)?.imageUrl
  );
  return recentTasks.map(task => (task.resultData as GenerateImageResult).imageUrl);
});

function handleAttachmentReady() {
  if (!imageUrl.value) return;
  emit("done", imageUrl.value);
  imageUrl.value = "";
}

function generateImage() {
  if (!prompt.value.trim()) return;

  const payload = {
    content: prompt.value,
    url: "",
    html: prompt.value,
    campaignId: "",
    images: [],
    files: [],
    taskType: "website/tools/generate-image",
  };

  startProgress();

  mutate(
    {
      taskType: "website/tools/generate-image",
      formData: payload,
    },
    {
      onSuccess(data) {
        taskId.value = data.id;
        if (progress.value < 50) setProgress(50);
      },
      onError() {
        $toast.error("Failed to generate image");
        endProgress();
      },
    }
  );
}

watch(data, d => {
  if (!d) return "";
  const resultData = d.resultData as GenerateImageResult;
  imageUrl.value = resultData.imageUrl || "";
  const errorMessage = d.failedMessage;
  if (imageUrl.value) {
    setProgress(100);
    prompt.value = "";
    refetchContent();
    endProgress();
  } else if (errorMessage) {
    setProgress(100);
    endProgress();
    $toast.error(errorMessage);
  }
});
</script>
